import { type FC, useState } from 'react'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { LinkButton } from '../../../components/Button/LinkButton'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../../common/integrations'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useGetUserSchedulingSettingsQuery, useUpdateUserSchedulingSettingsMutation } from '../../../store/scheduling/scheduling'
import { type CalendarProvider, MeetingPlatformProvider } from '../../../models/Scheduling'
import { OnboardingStepHeader } from '../../Growth/Onboarding/OnboardingStepHeader/OnboardingStepHeader'
import { useIntegrations } from '../../Integration/useIntegrations'
import { ConnectedDetail, IntegrationOptionRow, ReconnectDetail } from './IntegrationOptionRow'
import { integrationConnectPath, INTEGRATION_SETTINGS_PATH } from '../../ProfileSettings/Scheduling/sections/schedulingIntegrationStatus'
import { OnboardingStepContent } from './OnboardingStepFrame'
import {
  CALENDAR_SERVICE_ID_BY_PROVIDER,
  getIntegrationEmail,
  IntegrationConnectionStatus,
  MEETING_PLATFORM_SERVICE_ID,
  type MeetingPlatformOnboardingOption,
  resolveMeetingPlatformOptions,
} from './onboardingIntegrations'
import styles from './MeetingPlatformStep.module.scss'

const RECONNECT_TOOLTIP = "Scheduling can't add join links for this platform yet. Reconnecting doesn't affect your meeting notes."
const SUBTITLE = 'Zocks adds a join link to every online booking.'
const RECONNECT_SUBTITLE = "Scheduling can't add join links to your bookings yet. Reconnecting doesn't affect your meeting notes."

export interface MeetingPlatformStepProps {
  onCompleted?: () => void
}

export const MeetingPlatformStep: FC<MeetingPlatformStepProps> = ({ onCompleted }) => {
  const dispatch = useAppDispatch()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { accountId, id: userId } = authenticatedUser
  const { data: me } = useGetMeQuery({ accountId })
  const { data: userSettings } = useGetUserSchedulingSettingsQuery({ accountId, userId })
  const { isIntegrationAllowed, isLoading: isIntegrationsLoading } = useIntegrations()
  const [updateUserSettings, { isLoading: isSelecting }] = useUpdateUserSchedulingSettingsMutation()
  const [selectedProvider, setSelectedProvider] = useState<MeetingPlatformProvider>()

  if (!userSettings || isIntegrationsLoading) {
    return null
  }

  const options = resolveMeetingPlatformOptions(userSettings.integrations, isIntegrationAllowed)
  const nativeOption = options.find((option) => option.isNative)
  const zoomOption = options.find((option) => option.provider === MeetingPlatformProvider.ZOOM)
  const hasSelectableZoomOption = zoomOption != null && zoomOption.status !== IntegrationConnectionStatus.NOT_CONNECTED
  const availableOptions = [nativeOption, hasSelectableZoomOption ? zoomOption : undefined].filter(
    (option): option is MeetingPlatformOnboardingOption => option != null,
  )

  const selectPlatform = async (provider: MeetingPlatformProvider) => {
    const result = await updateUserSettings({
      accountId,
      userId,
      update: { integrations: { ...userSettings.integrations, selectedMeetingPlatform: provider } },
    })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not select the meeting platform', messages: ['Please try again.'] }))
    } else {
      onCompleted?.()
    }
  }

  const reconnectPathFor = (option: MeetingPlatformOnboardingOption): string =>
    option.isNative
      ? integrationConnectPath(CALENDAR_SERVICE_ID_BY_PROVIDER[userSettings.integrations.selectedCalendar as CalendarProvider])
      : integrationConnectPath(option.serviceId as IntegrationServiceId)

  const manageLink = (
    <LinkButton variant='primaryLinkButton' to={INTEGRATION_SETTINGS_PATH} className='text-decoration-none'>
      Manage your meeting platform integrations
    </LinkButton>
  )

  if (!nativeOption && !hasSelectableZoomOption) {
    return (
      <>
        <OnboardingStepHeader icon={<i className='bi bi-camera-video' />} title='Connect your meeting platform' subtitle={SUBTITLE} />
        {zoomOption ? (
          <OnboardingStepContent>
            <div
              className={classNames(
                'd-flex flex-column align-items-center text-center gap-2 border zocks-border-color-gray-400 zocks-border-1.0 px-3 zocks-py-1.25',
                styles.connectCard,
              )}
            >
              <IntegrationIcon serviceId={zoomOption.serviceId as IntegrationServiceId} width='1.25rem' height='1.25rem' />
              <span className='paragraph-medium fw-bold zocks-text-gray-900'>{zoomOption.label}</span>
              <LinkButton
                to={integrationConnectPath(zoomOption.serviceId as IntegrationServiceId)}
                variant='primaryLinkButton'
                className='text-decoration-none'
              >
                Connect
              </LinkButton>
            </div>
          </OnboardingStepContent>
        ) : (
          manageLink
        )}
      </>
    )
  }

  if (availableOptions.length === 1) {
    const singleOption = availableOptions[0]
    const isReconnect = singleOption.status === IntegrationConnectionStatus.NEEDS_RECONNECT
    const iconServiceId = singleOption.serviceId ?? MEETING_PLATFORM_SERVICE_ID[singleOption.provider]

    return (
      <>
        <OnboardingStepHeader
          icon={<i className='bi bi-camera-video' />}
          title={isReconnect ? 'Allow Scheduling to add join links' : 'Confirm your meeting platform'}
          subtitle={isReconnect ? RECONNECT_SUBTITLE : SUBTITLE}
        />
        <OnboardingStepContent>
          <div className='d-flex flex-column gap-3 p-3 border rounded-3 zocks-border-color-gray-300 w-100'>
            <div className='d-flex align-items-center gap-2'>
              {iconServiceId ? <IntegrationIcon serviceId={iconServiceId} width='1.5rem' height='1.5rem' /> : null}
              <span className='d-flex flex-column overflow-hidden'>
                <span className='paragraph-small fw-bold zocks-text-gray-900'>{singleOption.label}</span>
                {singleOption.isNative ? (
                  <span className='paragraph-small zocks-text-gray-600'>{singleOption.viaLabel}</span>
                ) : isReconnect ? (
                  <ReconnectDetail email={getIntegrationEmail(me?.integrations, singleOption.serviceId as IntegrationServiceId)} tooltip={RECONNECT_TOOLTIP} />
                ) : (
                  <ConnectedDetail email={getIntegrationEmail(me?.integrations, singleOption.serviceId as IntegrationServiceId)} />
                )}
              </span>
            </div>
            {isReconnect ? (
              <LinkButton to={reconnectPathFor(singleOption)} variant='primary' className='align-self-stretch'>
                Reconnect
              </LinkButton>
            ) : (
              <Button variant='primary' className='align-self-stretch' loading={isSelecting} onClick={() => void selectPlatform(singleOption.provider)}>
                Select platform
              </Button>
            )}
          </div>
          {singleOption.isNative && zoomOption ? manageLink : null}
        </OnboardingStepContent>
      </>
    )
  }

  const selectedOption =
    options.find((option) => option.provider === selectedProvider) ??
    options.find((option) => option.provider === userSettings.integrations.selectedMeetingPlatform)
  const isSelectedReconnect = selectedOption?.status === IntegrationConnectionStatus.NEEDS_RECONNECT

  return (
    <>
      <OnboardingStepHeader icon={<i className='bi bi-camera-video' />} title='Choose your meeting platform' subtitle={SUBTITLE} />
      <OnboardingStepContent>
        <div role='radiogroup' aria-label='Meeting platform' className='d-flex flex-column gap-2 w-100'>
          {options.map((option) => (
            <IntegrationOptionRow
              key={option.provider}
              serviceId={option.serviceId ?? MEETING_PLATFORM_SERVICE_ID[option.provider]}
              label={option.label}
              detail={
                option.isNative ? (
                  <span className='zocks-text-gray-600'>{option.viaLabel}</span>
                ) : option.status === IntegrationConnectionStatus.READY ? (
                  <ConnectedDetail email={getIntegrationEmail(me?.integrations, option.serviceId as IntegrationServiceId)} />
                ) : (
                  <ReconnectDetail email={getIntegrationEmail(me?.integrations, option.serviceId as IntegrationServiceId)} tooltip={RECONNECT_TOOLTIP} />
                )
              }
              selected={option.provider === selectedOption?.provider}
              onSelect={() => setSelectedProvider(option.provider)}
            />
          ))}
        </div>
        {selectedOption && isSelectedReconnect ? (
          <LinkButton to={reconnectPathFor(selectedOption)} variant='primary' className='align-self-stretch'>
            Reconnect
          </LinkButton>
        ) : (
          <Button
            variant='primary'
            className='align-self-stretch'
            disabled={!selectedOption}
            loading={isSelecting}
            onClick={() => (selectedOption ? void selectPlatform(selectedOption.provider) : undefined)}
          >
            Select platform
          </Button>
        )}
      </OnboardingStepContent>
    </>
  )
}
